import React, { useMemo, useState } from "react";
import moment from "moment";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import useNow from "../tablet-device/transfer/useNow";
import LinkedVehicleMap from "./LinkedVehicleMap";
import LinkedVehicleDetailsPanel from "./LinkedVehicleDetailsPanel";
import { resolveGatewaySite } from "./linkedVehicleUtils";

export const LINKED_VEHICLE_SECTION_ID = "linked-vehicle-location";

// Same surfaces as the tablet details page (tablet-device/index.js).
const surfaceSx = ({ palette, borders, functions: { rgba } }) => ({
  backgroundColor: palette.background?.card || palette.background.default,
  border: `1px solid ${rgba(palette.text.main, 0.12)}`,
  borderRadius: borders.borderRadius.lg,
  boxShadow: "none",
});

const nestedSurfaceSx = ({ palette, borders, functions: { rgba } }) => ({
  backgroundColor: rgba(palette.text.main, 0.035),
  border: `1px solid ${rgba(palette.text.main, 0.1)}`,
  borderRadius: borders.borderRadius.md,
  boxShadow: "none",
  overflow: "hidden",
});

export default function LinkedVehicleCard({
  vehicles = [],
  device,
  deviceSetupFields,
  paramValue,
  selectedId,
  onSelect,
}) {
  const now = useNow(15000, vehicles.length > 0);
  const [internalId, setInternalId] = useState(null);
  const site = useMemo(
    () => resolveGatewaySite({ device, deviceSetupFields, paramValue }),
    [device, deviceSetupFields, paramValue]
  );

  const activeId = selectedId ?? internalId;
  const selected = vehicles.find((vehicle) => vehicle.id === activeId) ?? vehicles[0];
  const handleSelect = onSelect ?? setInternalId;

  if (!selected) return null;

  const newestMs = Math.max(0, ...vehicles.map((vehicle) => vehicle.lastUpdatedMs ?? 0));
  const subtitle = [
    `${vehicles.length} linked GPS device${vehicles.length === 1 ? "" : "s"}`,
    newestMs ? `updated ${moment(newestMs).from(moment(now))}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <MDBox
      id={LINKED_VEHICLE_SECTION_ID}
      mb={3}
      p={{ xs: 2, md: 2.5 }}
      sx={(theme) => ({ ...surfaceSx(theme), scrollMarginTop: "96px" })}
    >
      <MDBox
        display="flex"
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        mb={2.5}
        flexWrap="wrap"
      >
        <MDBox>
          <MDTypography variant="h6" color="text" fontWeight="bold">
            <i className="fa-solid fa-truck" style={{ fontSize: 16, marginRight: 8 }} />
            Linked Vehicle Location
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {subtitle}
          </MDTypography>
        </MDBox>
      </MDBox>

      <MDBox
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        height={{ xs: "auto", md: 400 }}
        sx={nestedSurfaceSx}
      >
        <MDBox flex={1} minWidth={0} height={{ xs: 320, md: "100%" }} position="relative">
          <LinkedVehicleMap
            vehicles={vehicles}
            selected={selected}
            onSelect={handleSelect}
            site={site}
            now={now}
          />
        </MDBox>
        <LinkedVehicleDetailsPanel
          vehicles={vehicles}
          selected={selected}
          onSelect={handleSelect}
          site={site}
          now={now}
        />
      </MDBox>
    </MDBox>
  );
}
